import { SelectChangeEvent } from '@mui/material';
import { ChangeEvent, useMemo, useState } from 'react';

import {
  AdminEventTransactionsQuery,
  TransactionStatus,
  TransactionType,
} from '../../apollo/__generated__/graphql';
import { useAdminEventTransactionsQuery } from '../../event/hooks/useAdminEventTransactionsQuery';
import { useCurrentEventQuery } from '../../event/hooks/useCurrentEventQuery';
import { useStaffRefundTransactionMutation } from '../../transactions/hooks/useStaffRefundTransactionMutation';
import { downloadTransactionsAsCsv } from '../../transactions/services/downloadTransactionsAsCsv.service';

export interface DialogType {
  title: string;
  description: string;
  onSubmit: () => void;
}

export const useAdminEventTransactions = () => {
  const [search, setSearch] = useState<string>('');
  const [dialog, setDialog] = useState<DialogType | null>(null);
  const { data: currentEventData } = useCurrentEventQuery();
  const [transactionStatus, setTransactionStatus] = useState<
    TransactionStatus[]
  >([]);
  const [transactionTypes, setTransactionTypes] = useState<TransactionType[]>(
    [],
  );
  const { refundTransaction } = useStaffRefundTransactionMutation();

  const { data: eventTransactionsData, loading } =
    useAdminEventTransactionsQuery(currentEventData?.currentEvent?.id ?? null);

  const eventTransactions = eventTransactionsData?.eventTransactions ?? [];

  const onTransactionStatusChange = (
    event: SelectChangeEvent<TransactionStatus[]>,
  ) => {
    setTransactionStatus(event.target.value as TransactionStatus[]);
  };

  const onTransactionTypeChange = (
    event: SelectChangeEvent<TransactionType[]>,
  ) => {
    setTransactionTypes(event.target.value as TransactionType[]);
  };

  const transactions = useMemo(() => {
    return eventTransactions
      .filter(
        (transaction) =>
          transactionStatus.length === 0 ||
          transactionStatus.includes(transaction.status),
      )
      .filter(
        (transaction) =>
          transactionTypes.length === 0 ||
          transactionTypes.includes(transaction.type),
      )
      .filter((transaction) =>
        `${transaction.user.firstname} ${transaction.user.lastname} ${transaction.user.email} ${transaction.payer.firstname} ${transaction.payer.lastname} ${transaction.payer.email}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      );
  }, [eventTransactions, transactionStatus, transactionTypes, search]);

  const clearSearch = () => {
    setSearch('');
  };

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const onDownloadCsv = () => {
    downloadTransactionsAsCsv(transactions);
  };

  const onRefundPress = (
    transaction: AdminEventTransactionsQuery['eventTransactions'][number],
  ) => {
    setDialog({
      title: 'Rembourser la transaction',
      description:
        "Le statut de la transaction passera à REFUNDED et un email sera envoyé à l'utilisateur. Le remboursement ne sera pas effectué dans HelloAsso et devra être fait manuellement.",
      onSubmit: () => {
        refundTransaction(transaction.id);
        setDialog(null);
      },
    });
  };

  const onCloseDialog = () => {
    setDialog(null);
  };

  return {
    transactions,
    loading,
    dialog,
    onCloseDialog,
    onRefundPress,
    onDownloadCsv,
    onTransactionStatusChange,
    onTransactionTypeChange,
    transactionStatus,
    transactionTypes,
    search,
    clearSearch,
    onSearchChange,
  };
};
